/**
 * Plain-English glossary — every metric and AI term a business owner sees
 * should be explainable in one hover, in owner language, not data-science
 * language. Keyed by KPI id (for the ribbon) plus general terms used across
 * widgets. Keep entries to one sentence; if it needs two, the metric name
 * is the problem.
 */

export const KPI_GLOSSARY: Record<string, string> = {
  revenue_collected: 'Cash actually received this period — payments that cleared, not invoices sent.',
  pipeline_value: 'The total dollar value of every open deal, before adjusting for how likely each is to close.',
  evidence_forecast: 'The part of your open pipeline the evidence supports: deals that said no in writing or went dark are taken out, each with the reason shown.',
  deals_at_risk: 'Open deals flagged as likely to stall or be lost — from inactivity, slipped dates or weak engagement in your own history. A trained model replaces that estimate once it beats it.',
  followups_due: 'Conversations waiting on you — emails or threads where the next move is yours.',
  projected_revenue: 'Where this period likely lands: cash collected so far plus open deals weighted by their win probability.',
  revenue_goal: 'The revenue target you set for this period in Settings.',
  weekly_close_rate: 'The share of open pipeline you would need to win each week to still hit your goal.',
  active_clients: 'Clients with recent activity — meetings, emails, invoices, or open work.',
  escalations: 'Items the AI thinks need your attention today and has drafted a response for.',
  win_rate: 'Of deals that closed recently, the percentage you won.',
  total_deal_value: 'Combined value of deals in late stages — the money closest to landing.',
};

export const TERM_GLOSSARY: Record<string, string> = {
  'health score': 'A 0–100 score of how safe a client relationship is, from engagement, payments, and meeting signals.',
  'churn risk': 'The model’s estimate of how likely a repeat client is to lapse: not buying again within one and a half times their usual gap between purchases (60 days at least), counted from their last purchase.',
  'win probability': 'The model’s estimate of how likely an open deal is to close-won, learned from your past deals.',
  'risk score': 'How likely a deal is to stall or be lost — higher means act sooner.',
  'weighted pipeline': 'Open deal value adjusted by each deal’s win probability — a realistic forecast, not a best case.',
  'concentration risk': 'How much revenue depends on your biggest client — high concentration means one departure hurts.',
  'momentum': 'Whether a deal or relationship is heating up or cooling down, from meeting and email signals.',
  'cash runway': 'How long current cash lasts at your current burn rate.',
  'ar aging': 'Unpaid invoices grouped by how overdue they are — the further right, the harder to collect.',
  'confidence': 'How sure the model is about this prediction, based on how much similar history it has seen.',
};

/** Hint for a KPI ribbon card, by KPI id. */
export function kpiHint(id: string): string | undefined {
  return KPI_GLOSSARY[id];
}
